import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Software & Web Development", "IT Consulting & Support", "Cloud & DevOps", "Something else"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", service: SERVICES[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", service: SERVICES[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors";

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Let's talk"
        subtitle="Tell us about your project and we'll get back within one business day. No sales scripts — just engineers who can help."
      />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-3">
          <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Name</label>
                <input data-testid="contact-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Email</label>
                <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="jane@company.com" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Service needed</label>
              <select data-testid="contact-service" className={inputCls} value={form.service} onChange={update("service")}>
                {SERVICES.map((s) => <option key={s} value={s} className="bg-[#0A0A0A]">{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Message</label>
              <textarea data-testid="contact-message" rows={6} className={inputCls} value={form.message} onChange={update("message")} placeholder="Tell us what you're building..." />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-cyan-500 text-black font-semibold px-8 py-4 rounded-full hover:bg-cyan-400 hover:scale-105 transition-transform disabled:opacity-60 disabled:hover:scale-100"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Send message <ArrowUpRight size={18} /></>}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-2">
          <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-8 h-full">
            <p className="font-mono-j text-xs tracking-[0.2em] uppercase text-cyan-400 mb-8">Reach us directly</p>
            <ul className="space-y-7">
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-xl bg-cyan-500/10 grid place-items-center shrink-0"><Mail className="text-cyan-400" size={20} /></span>
                <div><p className="text-sm text-zinc-500">Email</p><p className="text-white">hello@blueverait.com</p></div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-xl bg-cyan-500/10 grid place-items-center shrink-0"><Phone className="text-cyan-400" size={20} /></span>
                <div><p className="text-sm text-zinc-500">Phone</p><p className="text-white">+91 98765 43210</p></div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-xl bg-cyan-500/10 grid place-items-center shrink-0"><MapPin className="text-cyan-400" size={20} /></span>
                <div><p className="text-sm text-zinc-500">Office</p><p className="text-white">Bengaluru, India</p></div>
              </li>
            </ul>
          </div>
        </Reveal>
      </section>
    </>
  );
}
